<template>
  <div class="inq-page">
    <APageHead title="문의 관리" sub="사용자 문의 내역을 관리하고 답변합니다." />

    <div class="inq-page__stats">
      <ACard><AStat label="전체 문의" :value="statistics.total" unit="건" /></ACard>
      <ACard><AStat label="대기 중" :value="statistics.pending" unit="건" /></ACard>
      <ACard><AStat label="처리 중" :value="statistics.processing" unit="건" /></ACard>
      <ACard><AStat label="답변 완료" :value="statistics.completed" unit="건" /></ACard>
    </div>

    <ACard overflow="visible">
      <div class="inq-page__filters">
        <div class="p-field">
          <label class="p-field__label"><i class="material-icons-round p-field__label-icon">search</i>검색</label>
          <input v-model="searchQuery" type="text" class="p-input" placeholder="제목·내용·이름 검색..." @input="onSearchInput" />
        </div>
        <div class="p-field">
          <label class="p-field__label"><i class="material-icons-round p-field__label-icon">category</i>카테고리</label>
          <select v-model="selectedCategory" class="p-input" @change="applyFilters">
            <option value="">전체</option>
            <option v-for="c in categories" :key="c.value" :value="c.value">{{ $t(c.labelKey) }}</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label"><i class="material-icons-round p-field__label-icon">tune</i>상태</label>
          <select v-model="statusFilter" class="p-input" @change="applyFilters">
            <option value="">전체</option>
            <option value="pending">대기</option>
            <option value="processing">처리중</option>
            <option value="completed">답변완료</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label"><i class="material-icons-round p-field__label-icon">flag</i>우선순위</label>
          <select v-model="priorityFilter" class="p-input" @change="applyFilters">
            <option value="">전체</option>
            <option value="urgent">긴급</option>
            <option value="high">높음</option>
            <option value="medium">보통</option>
            <option value="low">낮음</option>
          </select>
        </div>
        <div class="p-field">
          <label class="p-field__label"><i class="material-icons-round p-field__label-icon">sort</i>정렬</label>
          <select v-model="sortBy" class="p-input" @change="applyFilters">
            <option value="latest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="priority">우선순위순</option>
          </select>
        </div>
      </div>
    </ACard>

    <ACard>
      <template #title>
        <span class="inq-page__sec-title"><i class="material-icons-round inq-page__sec-icon">mail</i>문의 목록</span>
      </template>
      <template #action>
        <APill kind="info">{{ pagination.total }}건</APill>
      </template>

      <!-- 스피너는 보여줄 목록이 없을 때만. 페이지 이동·검색 중에는 기존 목록을 흐리게 유지해 높이가 튀지 않게 한다 -->
      <div v-if="loading && inquiries.length === 0" class="inq-page__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>문의 목록을 불러오는 중...</p>
      </div>

      <AEmpty v-else-if="inquiries.length === 0" title="검색 결과가 없어요" />

      <div v-else :class="['inq-page__list', { 'is-loading': loading }]">
        <article
          v-for="(inq, idx) in inquiries"
          :key="inq.id"
          :class="['inq-page__item', { 'is-priority': inq.priority === 'urgent' || inq.priority === 'high' }]"
          @click="viewInquiry(inq)"
        >
          <span class="inq-page__no">{{ rowNo(idx) }}</span>
          <div class="inq-page__item-body">
            <div class="inq-page__item-meta">
              <APill kind="yellow">{{ getCategoryLabel(inq.category) }}</APill>
              <APill :kind="statusPill(inq.status)" dot>{{ statusLabel(inq.status) }}</APill>
              <APill :kind="priorityPill(inq.priority)">{{ priorityLabel(inq.priority) }}</APill>
            </div>
            <h6 class="inq-page__subject">{{ inq.subject }}</h6>
            <p class="inq-page__content">{{ inq.content }}</p>
            <div class="inq-page__item-foot">
              <span><i class="material-icons-round">person</i>{{ inq.user_name }}</span>
              <span><i class="material-icons-round">mail</i>{{ inq.user_email }}</span>
              <span><i class="material-icons-round">schedule</i>{{ formatDate(inq.created_at) }}</span>
              <span v-if="attachmentsOf(inq).length" :title="`첨부파일 ${attachmentsOf(inq).length}개`">
                <i class="material-icons-round">attach_file</i>{{ attachmentsOf(inq).length }}
              </span>
            </div>
          </div>
          <ABtn
            size="sm"
            :kind="inq.status === 'completed' ? 'ghost' : 'primary'"
            :icon="inq.status === 'completed' ? 'visibility' : 'reply'"
            @click.stop="openReplyModal(inq)"
          />
        </article>
      </div>

      <APagination
        v-if="pagination.pages > 1"
        :current-page="currentPage"
        :total="pagination.total"
        :page-size="pagination.limit"
        @change="goPage"
      />
    </ACard>

    <AModal v-model="showReplyModal" size="lg">
      <template #title>
        <span class="inq-page__modal-title">
          <i class="material-icons-round inq-page__modal-icon">
            {{ selectedInquiry?.status === 'completed' ? 'mark_email_read' : 'edit_note' }}
          </i>
          {{ selectedInquiry?.status === 'completed' ? '문의 상세보기' : '문의 답변하기' }}
        </span>
      </template>
      <template #sub>
        {{ selectedInquiry?.user_name }}님의 문의
      </template>

      <div v-if="selectedInquiry" class="inq-page__detail">
        <div class="inq-page__detail-meta">
          <APill kind="yellow">{{ getCategoryLabel(selectedInquiry.category) }}</APill>
          <APill :kind="statusPill(selectedInquiry.status)" dot>{{ statusLabel(selectedInquiry.status) }}</APill>
          <APill :kind="priorityPill(selectedInquiry.priority)">{{ priorityLabel(selectedInquiry.priority) }}</APill>
        </div>
        <h6 class="inq-page__detail-subject">{{ selectedInquiry.subject }}</h6>
        <p class="inq-page__detail-content">{{ selectedInquiry.content }}</p>

        <div v-if="attachmentsOf(selectedInquiry).length" class="inq-page__files">
          <div class="inq-page__files-label">
            <i class="material-icons-round">attach_file</i>첨부파일 {{ attachmentsOf(selectedInquiry).length }}개
          </div>
          <ul class="inq-page__file-list">
            <li v-for="att in attachmentsOf(selectedInquiry)" :key="att.id" class="inq-page__file">
              <button
                v-if="isImage(att)"
                type="button"
                class="inq-page__thumb"
                :title="`${att.file_name} 새 탭에서 열기`"
                @click="openAttachment(att)"
              >
                <img v-if="attachmentUrls[att.id]" :src="attachmentUrls[att.id]" :alt="att.file_name" />
                <i v-else-if="attachmentErrors[att.id]" class="material-icons-round">broken_image</i>
                <span v-else class="p-spinner"></span>
              </button>
              <i v-else class="material-icons-round inq-page__file-icon">{{ fileIcon(att) }}</i>
              <div class="inq-page__file-info">
                <span class="inq-page__file-name" :title="att.file_name">{{ att.file_name }}</span>
                <small>{{ formatFileSize(att.size) }}</small>
              </div>
              <ABtn v-if="canOpenInline(att)" size="sm" icon="open_in_new" title="새 탭에서 열기" @click="openAttachment(att)" />
              <ABtn size="sm" icon="download" title="다운로드" :loading="!!downloadingIds[att.id]" @click="downloadAttachment(att)" />
            </li>
          </ul>
        </div>
        <div class="inq-page__detail-foot">
          <span><i class="material-icons-round">person</i>{{ selectedInquiry.user_name }}</span>
          <span><i class="material-icons-round">mail</i>{{ selectedInquiry.user_email }}</span>
          <span><i class="material-icons-round">schedule</i>{{ formatDate(selectedInquiry.created_at) }}</span>
        </div>

        <div v-if="selectedInquiry.status === 'completed' && selectedInquiry.reply" class="inq-page__answer">
          <div class="inq-page__answer-label">
            <i class="material-icons-round">check_circle</i>답변 내용
          </div>
          <p>{{ selectedInquiry.reply }}</p>
          <small>답변 일시: {{ formatDate(selectedInquiry.reply_date) }}</small>
        </div>

        <form v-else class="inq-page__reply-form" @submit.prevent="saveReply">
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">toggle_on</i>
              처리 상태 <span class="p-field__req">*</span>
            </label>
            <select v-model="replyForm.status" class="p-input" required>
              <option value="processing">처리중</option>
              <option value="completed">답변완료</option>
            </select>
          </div>

          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">chat</i>
              답변 내용 <span class="p-field__req">*</span>
            </label>
            <textarea
              v-model="replyForm.reply"
              class="p-input p-input--area"
              rows="8"
              placeholder="고객님의 문의에 대한 상세한 답변을 작성하세요."
              required
            ></textarea>
            <div class="p-field__hint"><i>info</i>답변은 고객 이메일로 자동 발송됩니다.</div>
          </div>
        </form>
      </div>

      <template #foot>
        <ABtn @click="showReplyModal = false">취소</ABtn>
        <ABtn
          v-if="selectedInquiry && selectedInquiry.status !== 'completed'"
          kind="primary" icon="send"
          @click="saveReply"
        >답변 전송</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import APageHead from "@/components/Pineapple/APageHead.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import APill from "@/components/Pineapple/APill.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import { formatServerDateTime } from "@/utils/datetime";

const SEARCH_DEBOUNCE_MS = 300;

export default {
  name: "HelpDeskInquiry",
  components: { APageHead, ABtn, ACard, AStat, APill, AEmpty, APagination, AModal },
  data() {
    return {
      // 서버 페이징 — 검색·필터·정렬·페이지는 모두 서버가 처리
      // (docs/헬프데스크_문의관리_API.md §2)
      inquiries: [],
      pagination: { total: 0, page: 1, limit: 10, pages: 0 },
      statistics: { total: 0, pending: 0, processing: 0, completed: 0 },
      categories: [
        { value: "general", labelKey: "helpdesk_category.general" },
        { value: "payment", labelKey: "helpdesk_category.payment" },
        { value: "technical", labelKey: "helpdesk_category.technical" },
        { value: "account", labelKey: "helpdesk_category.account" },
        { value: "content", labelKey: "helpdesk_category.content" },
        { value: "other", labelKey: "helpdesk_category.other" },
      ],
      loading: false,
      searchQuery: "",
      selectedCategory: "",
      statusFilter: "",
      priorityFilter: "",
      sortBy: "latest",
      currentPage: 1,
      itemsPerPage: 10,
      searchTimer: null,
      requestSeq: 0,
      selectedInquiry: null,
      showReplyModal: false,
      replyForm: { status: "processing", reply: "" },
      // 첨부파일 — 다운로드 URL 은 인증 헤더가 필요해 blob 으로 받아 object URL 로 쓴다
      // (docs/헬프데스크_문의관리_API.md §4.3)
      attachmentUrls: {},
      attachmentErrors: {},
      downloadingIds: {},
      attachmentSeq: 0,
    };
  },
  watch: {
    showReplyModal(open) {
      if (!open) this.releaseAttachments();
    },
  },
  created() {
    // 같은 첨부를 썸네일·열기·다운로드가 동시에 요청해도 한 번만 받도록 진행 중 요청을 공유 (반응형 불필요)
    this.attachmentPending = {};
  },
  mounted() { this.loadInquiries(); },
  beforeUnmount() {
    clearTimeout(this.searchTimer);
    this.releaseAttachments();
  },
  methods: {
    buildParams() {
      const params = { page: this.currentPage, limit: this.itemsPerPage, sort: this.sortBy };
      const search = this.searchQuery.trim();
      if (search) params.search = search;
      if (this.selectedCategory) params.category = this.selectedCategory;
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.priorityFilter) params.priority = this.priorityFilter;
      return params;
    },
    async loadInquiries() {
      // 검색어 입력 중 늦게 도착한 이전 응답이 최신 결과를 덮지 않도록 요청 순번으로 거른다
      const seq = ++this.requestSeq;
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: "/helpdesk/inquiries",
          params: this.buildParams(),
          loadingBar: false,
        });
        if (seq !== this.requestSeq) return;

        const data = res.data || {};
        this.inquiries = data.inquiries || [];

        const p = data.pagination || {};
        const total = p.total ?? this.inquiries.length;
        const limit = p.limit ?? this.itemsPerPage;
        this.pagination = { total, page: p.page ?? this.currentPage, limit, pages: p.pages ?? Math.ceil(total / limit) };

        const s = data.statistics || {};
        const pending = s.pending ?? 0;
        const processing = s.processing ?? 0;
        const completed = s.completed ?? 0;
        // statistics.total 은 이번 백엔드 수정분 — 배포 전 서버에는 없어 상태별 합으로 대신한다
        this.statistics = { total: s.total ?? pending + processing + completed, pending, processing, completed };

        // 답변 처리 등으로 마지막 페이지가 비면 남아 있는 마지막 페이지로 옮긴다
        if (this.inquiries.length === 0 && this.currentPage > 1 && this.pagination.pages < this.currentPage) {
          this.currentPage = Math.max(1, this.pagination.pages);
          this.loadInquiries();
        }
      } catch (error) {
        if (seq !== this.requestSeq) return;
        // 실패하면 목록은 이전 페이지 그대로이므로 페이지 강조도 되돌린다
        this.currentPage = this.pagination.page;
        console.error("문의 로딩 실패:", error);
        this.$swal({ icon: "error", title: "로딩 실패", text: "문의 목록을 불러오는데 실패했습니다." });
      } finally {
        if (seq === this.requestSeq) this.loading = false;
      }
    },
    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(this.applyFilters, SEARCH_DEBOUNCE_MS);
    },
    applyFilters() {
      clearTimeout(this.searchTimer);
      this.currentPage = 1;
      this.loadInquiries();
    },
    goPage(page) {
      this.currentPage = page;
      this.loadInquiries();
    },
    // 게시판식 역순 번호 — 조회 조건 전체 건수에서 현재 행의 위치를 뺀 값
    rowNo(idx) {
      return this.pagination.total - (this.pagination.page - 1) * this.pagination.limit - idx;
    },
    statusPill(s) { return { pending: "default", processing: "info", completed: "ok" }[s] || "default"; },
    statusLabel(s) { return { pending: "대기", processing: "처리중", completed: "답변완료" }[s] || s; },
    priorityPill(p) { return { urgent: "err", high: "warn", medium: "info", low: "default" }[p] || "default"; },
    priorityLabel(p) { return { urgent: "긴급", high: "높음", medium: "보통", low: "낮음" }[p] || p; },
    viewInquiry(inq) { this.openReplyModal(inq); },
    openReplyModal(inq) {
      this.releaseAttachments();
      this.selectedInquiry = inq;
      this.replyForm = { status: inq.status === "pending" ? "processing" : inq.status, reply: "" };
      this.showReplyModal = true;
      this.loadImagePreviews(inq);
    },
    // 배포 전 서버 응답에는 attachments 키가 없다
    attachmentsOf(inq) { return (inq && inq.attachments) || []; },
    isImage(att) { return (att.mime_type || "").startsWith("image/"); },
    canOpenInline(att) { return this.isImage(att) || att.mime_type === "application/pdf"; },
    fileIcon(att) { return att.mime_type === "application/pdf" ? "picture_as_pdf" : "description"; },
    fetchAttachmentUrl(att) {
      if (this.attachmentUrls[att.id]) return Promise.resolve(this.attachmentUrls[att.id]);
      if (this.attachmentPending[att.id]) return this.attachmentPending[att.id];

      const seq = this.attachmentSeq;
      // url 은 전체 URL 이고 $axios 는 api_url + url 로 붙이므로 origin 과 경로로 나눠 넘긴다
      let parsed;
      try {
        parsed = new URL(att.url);
      } catch (error) {
        return Promise.reject(error);
      }
      const { origin, pathname, search } = parsed;
      const promise = this.$axios({
        method: "get",
        api_url: origin,
        url: pathname + search,
        responseType: "blob",
        loadingBar: false,
      }).then((res) => {
        // 받는 사이 모달을 닫았거나 다른 문의로 바뀌었으면 버린다 (null)
        if (seq !== this.attachmentSeq) return null;
        const objectUrl = URL.createObjectURL(res.data);
        this.attachmentUrls[att.id] = objectUrl;
        return objectUrl;
      }).finally(() => {
        if (seq === this.attachmentSeq) delete this.attachmentPending[att.id];
      });
      this.attachmentPending[att.id] = promise;
      return promise;
    },
    loadImagePreviews(inq) {
      this.attachmentsOf(inq).filter(this.isImage).forEach((att) => {
        const seq = this.attachmentSeq;
        this.fetchAttachmentUrl(att).catch((error) => {
          if (seq !== this.attachmentSeq) return;
          console.error("첨부 미리보기 실패:", error);
          this.attachmentErrors[att.id] = true;
        });
      });
    },
    async openAttachment(att) {
      // 받아온 뒤 window.open 하면 팝업 차단에 걸리므로 탭을 먼저 열어 두고 주소를 넣는다
      const win = window.open("", "_blank");
      if (!win) {
        this.downloadAttachment(att);
        return;
      }
      try {
        const url = await this.fetchAttachmentUrl(att);
        if (url) win.location.href = url;
        else win.close();
      } catch (error) {
        win.close();
        this.notifyAttachmentError(error);
      }
    },
    async downloadAttachment(att) {
      if (this.downloadingIds[att.id]) return;
      this.downloadingIds[att.id] = true;
      try {
        const url = await this.fetchAttachmentUrl(att);
        if (!url) return;
        const link = document.createElement("a");
        link.href = url;
        link.download = att.file_name || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        this.notifyAttachmentError(error);
      } finally {
        delete this.downloadingIds[att.id];
      }
    },
    notifyAttachmentError(error) {
      console.error("첨부파일 받기 실패:", error);
      const text = error?.response?.status === 404 ? "첨부파일을 찾을 수 없습니다." : "첨부파일을 불러오지 못했습니다.";
      this.$swal({ icon: "error", title: "첨부파일 오류", text });
    },
    releaseAttachments() {
      this.attachmentSeq++;
      Object.values(this.attachmentUrls).forEach((url) => URL.revokeObjectURL(url));
      this.attachmentUrls = {};
      this.attachmentErrors = {};
      this.attachmentPending = {};
      this.downloadingIds = {};
    },
    formatFileSize(bytes) {
      if (!Number.isFinite(bytes)) return "";
      if (bytes < 1024) return `${bytes} B`;
      // 반올림하면 1024.0 KB 가 되는 구간은 MB 로 올린다
      if (bytes < 1023.95 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    async saveReply() {
      try {
        await this.$axios({
          method: "put",
          url: `/helpdesk/inquiries/${this.selectedInquiry.id}/reply`,
          data: this.replyForm, loadingBar: true,
        });
        this.$swal({ icon: "success", title: "답변 완료", text: "답변이 성공적으로 전송되었습니다." });
        this.showReplyModal = false;
        this.loadInquiries();
      } catch (error) {
        console.error("답변 저장 실패:", error);
        this.$swal({ icon: "error", title: "전송 실패", text: "답변 전송에 실패했습니다." });
      }
    },
    formatDate(s) {
      // +00:00 이 붙은 UTC 는 KST 로 변환, 표기 없는 값(배포 전 서버)은 KST 로 해석
      return formatServerDateTime(s, { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" }) || "-";
    },
    getCategoryLabel(v) {
      const c = this.categories.find((x) => x.value === v);
      return c ? this.$t(c.labelKey) : v;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.inq-page {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__filters {
    display: grid;
    grid-template-columns: 2fr repeat(4, 1fr);
    gap: 12px;
    @media (max-width: 992px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__sec-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; font-weight: 800; color: $p-ink;
  }

  &__sec-icon { color: $p-point-deep; font-size: 16px !important; }

  &__loading {
    display: flex; flex-direction: column; align-items: center; gap: 12px;
    padding: 48px 24px; color: $p-ink-mid;
    p { margin: 0; font-size: 12px; }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: opacity $p-dur $p-ease;

    &.is-loading {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__no {
    flex-shrink: 0;
    min-width: 32px;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink-soft;
    text-align: center;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 16px;
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    cursor: pointer;
    transition: border-color $p-dur $p-ease, background $p-dur $p-ease;

    &:hover {
      border-color: $p-point-deep;
      background: $p-panel-alt;
    }

    &.is-priority {
      border-left: 3px solid $p-err;
    }
  }

  &__item-body { flex: 1; min-width: 0; }

  &__item-meta {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    margin-bottom: 8px;
  }

  &__subject {
    margin: 0 0 4px;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
  }

  &__content {
    margin: 0 0 8px;
    font-size: 12px;
    color: $p-ink-mid;
    line-height: 1.55;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-clamp: 2;
  }

  &__item-foot {
    display: flex;
    gap: 12px;
    font-size: 11px;
    color: $p-ink-soft;
    flex-wrap: wrap;

    > span {
      display: inline-flex;
      align-items: center;
      gap: 4px;

      > i { font-size: 13px !important; }
    }
  }

  // ── 모달 ──
  &__modal-title {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 15px; font-weight: 800; color: $p-ink;
  }

  &__modal-icon { color: $p-point-deep; font-size: 20px !important; }

  &__detail {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__detail-meta {
    display: flex; gap: 6px; flex-wrap: wrap;
  }

  &__detail-subject {
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    color: $p-ink;
  }

  &__detail-content {
    margin: 0;
    padding: 14px 16px;
    background: $p-panel-alt;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    font-size: 13px;
    color: $p-ink2;
    line-height: 1.65;
    white-space: pre-line;
  }

  &__detail-foot {
    display: flex;
    gap: 12px;
    font-size: 11px;
    color: $p-ink-soft;
    flex-wrap: wrap;
    padding-bottom: 8px;
    border-bottom: 1px solid $p-line-soft;

    > span {
      display: inline-flex; align-items: center; gap: 4px;
      > i { font-size: 13px !important; }
    }
  }

  &__files {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__files-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 800;
    color: $p-ink-mid;

    > i { font-size: 14px !important; }
  }

  &__file-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__file {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-md;
    background: $p-panel;
  }

  &__thumb {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    padding: 0;
    overflow: hidden;
    border: 1px solid $p-line-soft;
    border-radius: $p-radius-sm;
    background: $p-panel-alt;
    color: $p-ink-soft;
    cursor: pointer;

    > img { width: 100%; height: 100%; object-fit: cover; }
  }

  &__file-icon {
    flex-shrink: 0;
    width: 56px;
    text-align: center;
    font-size: 28px !important;
    color: $p-ink-soft;
  }

  &__file-info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;

    > small { font-size: 11px; color: $p-ink-soft; }
  }

  &__file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
  }

  &__answer {
    padding: 14px 16px;
    background: $p-ok-soft;
    border-left: 3px solid $p-ok;
    border-radius: $p-radius-md;

    > p {
      margin: 0 0 8px;
      font-size: 13px;
      color: $p-ink;
      line-height: 1.7;
      white-space: pre-line;
    }

    > small {
      color: $p-ink-soft;
      font-size: 11px;
    }
  }

  &__answer-label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    font-weight: 800;
    color: $p-ok;
    margin-bottom: 8px;

    > i { font-size: 14px !important; }
  }

  &__reply-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 8px;
  }
}
</style>
